'use client';

import classNames from 'classnames';
import { useState, forwardRef, useImperativeHandle, useEffect } from 'react';
import { CmsImage } from '../../../components/CmsImage';
import { CmsSettings } from '../../../types/cmsSettings';
import { PushDataLayerFn } from '../../../types/pushDataLayer';
import { useCmsClientContext } from '../../../utils/cmsPageContext';
import { CmsNavigation } from '../types';
import { navbarLinkToUrl } from '../utils/utils';
import SidebarSection from './SidebarSection';

export interface SidebarRef {
  open: () => void;
  close: () => void;
}

type SidebarProps = {
  navigation: CmsNavigation;
  settings: CmsSettings;
  pushDataLayer: PushDataLayerFn;
};

// eslint-disable-next-line react/display-name
const Sidebar = forwardRef<SidebarRef, SidebarProps>((props, ref) => {
  const { langPrefix, brand } = useCmsClientContext();
  const [isOpen, setIsOpen] = useState(false);

  const { navigation, settings, pushDataLayer } = props;

  const {
    site_logo: { url: appLogo, alternativeText: appLogoAlt },
  } = settings;
  const { items = [] } = navigation;

  useImperativeHandle(ref, () => ({
    open: () => setIsOpen(true),
    close: () => setIsOpen(false),
  }));

  useEffect(() => {
    if (isOpen) {
      window.document.body.style.overflow = 'hidden';
      return;
    }

    window.document.body.style.overflow = 'auto';
  }, [isOpen]);

  return (
    <div
      className={classNames('fixed', {
        'top-0 w-screen h-screen z-[49]': isOpen,
      })}
    >
      <div
        className={classNames(
          'h-dvh overflow-auto',
          'fixed left-0 top-0 z-50 w-80',
          'py-4 shadow-lg transition-transform duration-300 bg-white lg:hidden',
          {
            'translate-x-0': isOpen,
            '-translate-x-full': !isOpen,
          },
        )}
      >
        <div className="flex items-center justify-between border-b border-gray-300 px-4 pb-5">
          {appLogo && (
            <CmsImage
              src={appLogo}
              sizes={{ _: 104 }}
              alt={appLogoAlt || 'logo'}
              width={104}
              height={32}
            />
          )}
          <svg
            onClick={() => setIsOpen(false)}
            xmlns="http://www.w3.org/2000/svg"
            className="size-6 cursor-pointer"
            viewBox="0 0 21 21"
          >
            <rect width="21" height="21" fill="none" />
            <path
              fill="none"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              d="m15.5 15.5l-10-10zm0-10l-10 10"
            />
          </svg>
        </div>
        {items!.map((menuItem) => {
          const { text, href } = navbarLinkToUrl(menuItem!, {
            langPrefix,
            brand,
          });
          return (
            <SidebarSection
              key={`${text}_${href}`}
              pushDataLayer={pushDataLayer}
              {...menuItem!}
            />
          );
        })}
      </div>
    </div>
  );
});

export default Sidebar;
